<template>
    <div class="selections calendar-selections">
        <SvgIcon icon="carbon/add" />
        <form @submit.prevent="submitForm">
            <div class="inputs">
                <div class="inputs__dropdown" :class="{ 'is-loading': isLoadingConfig }">
                    <div v-if="isLoadingConfig" class="icon spinner">
                        <img src="@/assets/icons/spinner.svg" class="icon__svg" />
                    </div>
                    <VSelect
                        v-model="v$.route.$model"
                        :options="filters?.routes"
                        :hasError="v$.route.$error"
                        :errors="[...new Set(v$.route.$errors.map((e) => e.$message))]"
                        label="Select route"
                    />
                </div>
                <div class="inputs__dropdown room" :class="{ 'is-loading': isLoading }">
                    <div v-if="isLoading" class="icon spinner">
                        <img src="@/assets/icons/spinner.svg" class="icon__svg" />
                    </div>
                    <VSelect
                        v-model="v$.cabin.$model"
                        canDeselect
                        :options="cabins"
                        :hasError="v$.cabin.$error"
                        :errors="[...new Set(v$.cabin.$errors.map((e) => e.$message))]"
                        :noOptionsLabel="
                            v$.route.$model === 'SCST'
                                ? 'No cabins available for selected route'
                                : 'Please select a route to view room type options'
                        "
                        label="Select room type"
                    />
                </div>
                <div class="inputs__dropdown" :class="{ 'is-loading': isLoadingConfig }">
                    <div v-if="isLoadingConfig" class="icon spinner">
                        <img src="@/assets/icons/spinner.svg" class="icon__svg" />
                    </div>
                    <VSelect
                        v-model="v$.vehicle.$model"
                        canDeselect
                        :options="filters?.vehicles"
                        :hasError="v$.vehicle.$error"
                        :errors="[...new Set(v$.vehicle.$errors.map((e) => e.$message))]"
                        label="Bring vehicle"
                    />
                </div>
            </div>
            <VBtn type="submit" :isLoading="isLoadingUpdate">Search</VBtn>
        </form>
    </div>
</template>

<script setup lang="ts">
import { AvailabilityFilters } from "@/modules/AvailabilityCalendar/models/AvailabilityFilter";
import useVuelidate from "@vuelidate/core";
import { helpers, required, requiredIf } from "@vuelidate/validators";
import { VBtn, VSelect } from "v-ferry-components";
import { computed, reactive } from "vue";
import useCabinsQuery from "../composables/useCabinsQuery";
import SvgIcon from "@/components/base/SvgIcon.vue";

interface Props {
    filters?: AvailabilityFilters;
    isLoadingConfig?: boolean;
    isLoadingUpdate?: boolean;
}

defineProps<Props>();

const emit = defineEmits(["updateFilters"]);

const state = reactive({
    route: "",
    cabin: "",
    vehicle: "",
});

const rules = {
    route: { required: helpers.withMessage("You must select a route", required) },
    cabin: {
        required: helpers.withMessage(
            "You must select either vehicle or room type",
            requiredIf(function () {
                return !state.vehicle;
            }),
        ),
    },
    vehicle: {
        required: helpers.withMessage(
            "You must select either vehicle or room type",
            requiredIf(function () {
                return !state.cabin;
            }),
        ),
    },
};

const v$ = useVuelidate(rules, state);

const route = computed(() => state.route);

const { data: cabins, isLoading } = useCabinsQuery(route);

const submitForm = async () => {
    const isValid = await v$.value.$validate();
    if (!isValid) return;

    emit("updateFilters", state);
};
</script>

<style lang="scss" scoped>
.selections {
    > form {
        display: flex;
        flex-flow: row wrap;
        justify-content: flex-end;
        gap: 20px;

        .inputs {
            width: 100%;
            display: flex;
            justify-content: space-evenly;
            flex-flow: row wrap;
            gap: 20px;

            &__dropdown {
                flex: 1;
                min-width: 200px;
                width: 100%;

                position: relative;

                &.is-loading {
                    pointer-events: none;
                }
                .spinner {
                    position: absolute;
                    z-index: 999;
                    right: 40px;
                    background: white;
                    top: 19px;
                    height: 1rem !important;
                    width: 1rem !important;
                }
            }

            .room {
                flex: 2;
            }
        }

        .v-btn {
            width: 100%;
            max-width: 150px;

            @include tablet {
                max-width: none;
            }
        }
    }
}
</style>
